import React, { useMemo, useRef, useState } from 'react';

import { Modal, notification } from 'antd';

import ls from 'local-storage';
import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserPermission } from '@contexts/UserContext';

import { getContactName, getModel } from '@hooks/API/Contact/UseContactsList';
import {
    forceReloadActivePackages,
    useActivePackages,
    useAddPlaylistToPackage,
    useDeletePlaylistFromActivePackage,
} from '@hooks/API/Packages/UsePackages';

import PackageForm from '@components/ActivePackages/PackageForm';
import BookSelectorPanel from '@components/BookSelectorForPackages/BookSelectorPanel';
import { PackageTargetHeader } from '@components/BookSelectorForPackages/PackageTargetHeader';
import NwDrawer from '@components/Gui/NwDrawer';
import { NwAddButton } from '@components/Gui/NwButton';
import NwButtonOption from '@components/Gui/NwButtonOption';
import NwIcon from '@components/Gui/NwIcon';
import NwObjectCounter from '@components/Gui/NwObjectCounter';
import PackageDrawer from '@components/PackageDrawer/PackageDrawer';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const LATEST_PACKAGE_STORAGE_KEY = 'nw_config_latest_package';

const MODAL_MAX_WIDTH = 620;
const MODAL_MARGIN = 16;

/* Le regole globali di _modals.less inchiodano mask e wrap sopra a tutto. Questa modale
   deve invece stare sotto ai drawer che si aprono dal pannello: la classe abbassa il wrap
   e gli mette lo sfondo scurito, visto che gira senza mask. */
const MODAL_WRAP_CLASS = 'select-books-for-package-modal';

/* Posizione e altezza arrivano dallo style inline: la modale è ancorata al pulsante,
   non centrata da antd. Qui resta solo il corpo che deve riempire il contenitore. */
const StyledModal = styled(Modal)`
    .ant-modal-content {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    /* Niente padding: il fondo grigio del pannello deve arrivare ai bordi della modale,
       lo stacco intorno alle card lo dà il pannello stesso. */
    .ant-modal-body {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        padding: 0;
        overflow: hidden;
    }
`;

/** Modale allineata al centro del pulsante, tenuta comunque dentro il viewport. */
const getModalLayout = (triggerElement, headerHeight) => {
    const viewportWidth = window.innerWidth;
    const width = Math.min(MODAL_MAX_WIDTH, viewportWidth - MODAL_MARGIN * 2);
    const triggerRect = triggerElement.getBoundingClientRect();
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const maxLeft = viewportWidth - width - MODAL_MARGIN;

    return {
        width,
        style: {
            left: Math.min(Math.max(triggerCenter - width / 2, MODAL_MARGIN), maxLeft),
            top: headerHeight + MODAL_MARGIN,
            height: window.innerHeight - headerHeight - MODAL_MARGIN * 2,
            maxWidth: 'none',
            margin: 0,
            paddingBottom: 0,
        },
    };
};

/** La modella dentro il package, se già presente. */
const findModelInPackage = (pack, contactID) =>
    pack && pack.Models ? pack.Models.find((model) => model.ModelID === contactID) : null;

/** Quanti book della modella sono già in quel package. */
const countModelBooksInPackage = (pack, contactID) => {
    const modelInPackage = findModelInPackage(pack, contactID);
    return modelInPackage && modelInPackage.Playlists ? modelInPackage.Playlists.length : 0;
};

/** I gruppi del package sono i Group distinti e non vuoti delle sue modelle, come groupPackage. */
const getPackageGroupNames = (pack) => {
    if (!pack || !pack.Models) {
        return [];
    }
    return [...new Set(pack.Models.map((model) => model.Group).filter((group) => group))];
};

/**
 * Icona nella card modella: apre il selettore di book su uno dei package attivi.
 * Qui vivono la scelta del package, quella del gruppo e le mutazioni; il corpo è il
 * pannello condiviso con i drawer del PackageDrawer.
 */
const BookSelectorButton = ({ modelID }) => {
    const contactID = Number(modelID);
    const { headerHeight, isMobile } = useLayoutContext();
    const { data: packages } = useActivePackages();
    const { mutateAsync: onAddPlaylistToPackage } = useAddPlaylistToPackage();
    const { mutateAsync: onDeletePlaylistFromActivePackage } = useDeletePlaylistFromActivePackage();

    const [open, setOpen] = useState(false);
    const [modalLayout, setModalLayout] = useState(null);
    const [targetPackageId, setTargetPackageId] = useState(() => ls.get(LATEST_PACKAGE_STORAGE_KEY));
    const [newPackageBookIDs, setNewPackageBookIDs] = useState(null);
    const [isPackageDetailOpen, setIsPackageDetailOpen] = useState(false);
    // Legata al package su cui è stata fatta: cambiando package torna al default senza effetti.
    const [groupChoice, setGroupChoice] = useState(null);
    const triggerRef = useRef(null);

    const model = getModel(contactID);
    const viewModelBooks = useUserPermission('ViewModelBooks', model && model.U && model.U.map((user) => user.UserID));

    const activePackages = useMemo(() => packages || [], [packages]);

    // Il package su cui si opera: l'ultimo usato se è ancora attivo, altrimenti il primo.
    const targetPackage = useMemo(() => {
        if (!activePackages.length) {
            return null;
        }
        return activePackages.find((pack) => pack.ID === targetPackageId) || activePackages[0];
    }, [activePackages, targetPackageId]);

    // PlaylistID -> PackagePlaylistID: la rimozione ha bisogno dell'id della riga nel package.
    const modelPackageBooks = useMemo(() => {
        const booksInPackage = new Map();
        const modelInPackage = findModelInPackage(targetPackage, contactID);
        if (modelInPackage && modelInPackage.Playlists) {
            modelInPackage.Playlists.forEach((playlist) => booksInPackage.set(playlist.PlaylistID, playlist.ID));
        }
        return booksInPackage;
    }, [targetPackage, contactID]);

    const addedPlaylists = useMemo(() => [...modelPackageBooks.keys()], [modelPackageBooks]);

    const groupNames = getPackageGroupNames(targetPackage);
    // La stessa modella non può stare in due gruppi: se è già nel package con un gruppo
    // quello è vincolato, e i book successivi la seguono lì.
    const modelGroup = findModelInPackage(targetPackage, contactID)?.Group;
    const isGroupLocked = Boolean(modelGroup);
    const defaultGroup = modelGroup || groupNames[0] || '';
    const selectedGroup =
        !isGroupLocked && groupChoice && targetPackage && groupChoice.packageID === targetPackage.ID
            ? groupChoice.group
            : defaultGroup;

    const notifyError = (description) => {
        notification.error({ message: 'Package not updated', description });
    };

    const handleChangeTargetPackage = (packageId) => {
        setTargetPackageId(packageId);
        ls.set(LATEST_PACKAGE_STORAGE_KEY, packageId);
    };

    const handleAddBook = async (book) => {
        // Senza package attivi il primo click ne crea uno con quel book già dentro.
        if (!targetPackage) {
            setNewPackageBookIDs([book.ID]);
            return;
        }
        try {
            await onAddPlaylistToPackage({
                data: { packageID: targetPackage.ID, playlistID: book.ID, PackageGroup: selectedGroup },
                isActivePackage: true,
            });
            await forceReloadActivePackages();
        } catch (error) {
            console.error('Add Book To Package Error => ', error);
            notifyError(`"${book.Name}" could not be added to the package.`);
        }
    };

    const handleRemoveBook = async (book) => {
        const packagePlaylistId = modelPackageBooks.get(book.ID);
        if (!targetPackage || !packagePlaylistId) {
            return;
        }
        try {
            await onDeletePlaylistFromActivePackage({
                packageID: targetPackage.ID,
                packagePlaylistId,
                isActivePackage: true,
            });
            await forceReloadActivePackages();
        } catch (error) {
            console.error('Remove Book From Package Error => ', error);
            notifyError(`"${book.Name}" could not be removed from the package.`);
        }
    };

    const handleAddedPackage = async (packageId) => {
        await forceReloadActivePackages();
        handleChangeTargetPackage(packageId);
    };

    const handleOpen = () => {
        if (!isMobile && triggerRef.current) {
            setModalLayout(getModalLayout(triggerRef.current, headerHeight));
        }
        setOpen(true);
    };

    const icon = (
        <NwIcon
            size="lg"
            pointer
            icon={byPrefixAndName.fasl['images']}
            badge={modelPackageBooks.size}
            primary={modelPackageBooks.size > 0}
        />
    );

    if (!viewModelBooks) {
        return <PermissionBlock permission={viewModelBooks} content={icon} />;
    }

    const title = `${getContactName(contactID)}'s books`;

    // L'icona apre il drawer di dettaglio del package scelto come destinazione: comodo per
    // controllare/gestire il package senza chiudere prima questa modale.
    const openPackageDetailIcon = targetPackage && (
        <NwIcon
            pointer
            icon={byPrefixAndName.fasl['sidebar-flip']}
            tooltip="package details"
            onClick={() => setIsPackageDetailOpen(true)}
        />
    );

    const packageSelector = () => {
        if (!activePackages.length) {
            return <div className="package-target-empty">no active packages</div>;
        }
        if (activePackages.length === 1) {
            return (
                <div className="package-target-row">
                    <div className="package-target-name">{activePackages[0].Name}</div>
                    {openPackageDetailIcon}
                </div>
            );
        }
        return (
            <div className="package-target-row">
                <div className="package-target-chips">
                    {activePackages.map((pack) => {
                        const booksCount = countModelBooksInPackage(pack, contactID);
                        return (
                            <NwButtonOption
                                key={pack.ID}
                                active={targetPackage && targetPackage.ID === pack.ID}
                                onClick={() => handleChangeTargetPackage(pack.ID)}
                            >
                                <span className="package-chip-name">{pack.Name}</span>
                                {booksCount > 0 && (
                                    <NwObjectCounter
                                        count={booksCount}
                                        style={{ display: 'flex', alignItems: 'center' }}
                                    />
                                )}
                            </NwButtonOption>
                        );
                    })}
                </div>
                {openPackageDetailIcon}
            </div>
        );
    };

    // Il package nuovo si crea dentro al pannello: il form prende il posto della testata
    // e le liste si nascondono, perché parlerebbero del package che sta per essere sostituito.
    const isCreatingPackage = newPackageBookIDs !== null;

    const header = isCreatingPackage ? (
        <PackageForm
            bookIDs={newPackageBookIDs}
            packageName={getContactName(contactID)}
            onAddedPackage={handleAddedPackage}
            onClose={() => setNewPackageBookIDs(null)}
        />
    ) : (
        <PackageTargetHeader>
            {packageSelector()}
            {groupNames.length > 0 && (
                <div className="package-target-group">
                    <NwIcon
                        icon={byPrefixAndName.fasl['people-group']}
                        tooltip={
                            isGroupLocked ? `already in "${modelGroup}": a model can't be in two groups` : null
                        }
                    />
                    {/* Con il gruppo vincolato le alternative restano visibili ma disabilitate:
                        si vede dov'è la modella e che le altre destinazioni sono precluse. */}
                    <div className="package-target-chips">
                        {groupNames.map((group) => (
                            <NwButtonOption
                                key={group}
                                active={selectedGroup === group}
                                disabled={isGroupLocked && selectedGroup !== group}
                                onClick={() => setGroupChoice({ packageID: targetPackage.ID, group })}
                            >
                                <span className="package-chip-name">{group}</span>
                            </NwButtonOption>
                        ))}
                        <NwButtonOption
                            active={selectedGroup === ''}
                            disabled={isGroupLocked && selectedGroup !== ''}
                            onClick={() => setGroupChoice({ packageID: targetPackage.ID, group: '' })}
                        >
                            <span className="package-chip-name">no group</span>
                        </NwButtonOption>
                    </div>
                </div>
            )}
        </PackageTargetHeader>
    );

    const panel = (
        <BookSelectorPanel
            addedPlaylists={addedPlaylists}
            contactID={contactID}
            destinationLabel={isCreatingPackage ? 'new package' : 'adding books to'}
            fillHeight={!isMobile}
            header={header}
            headerActions={
                isCreatingPackage ? null : (
                    <NwAddButton
                        ghost
                        size="small"
                        primary={!activePackages.length}
                        label="new package"
                        onClick={() => setNewPackageBookIDs([])}
                    />
                )
            }
            headerOnly={isCreatingPackage}
            packageGroup={selectedGroup}
            packageName={targetPackage ? targetPackage.Name : null}
            onAddBook={handleAddBook}
            onRemoveBook={handleRemoveBook}
        />
    );

    return (
        <>
            <span ref={triggerRef} onClick={handleOpen}>
                {icon}
            </span>
            {open &&
                (isMobile ? (
                    <NwDrawer title={title} padding="0" whiteBg={false} onClose={() => setOpen(false)}>
                        {panel}
                    </NwDrawer>
                ) : (
                    modalLayout && (
                        <StyledModal
                            open
                            mask={false}
                            wrapClassName={MODAL_WRAP_CLASS}
                            title={title}
                            footer={null}
                            width={modalLayout.width}
                            style={modalLayout.style}
                            onCancel={() => setOpen(false)}
                        >
                            {panel}
                        </StyledModal>
                    )
                ))}
            {isPackageDetailOpen && targetPackage && (
                <PackageDrawer packageId={targetPackage.ID} onClose={() => setIsPackageDetailOpen(false)} />
            )}
        </>
    );
};

export default BookSelectorButton;
